import queryString from "query-string";
import React, { useCallback, useMemo, useState } from "react";
import { defineMessage, defineMessages, injectIntl, IntlShape } from "react-intl";
import { v4 as uuidv4 } from "uuid";
import { withAuthorizationVerifier } from "../../components/authorization-verifier";
import { usePropertyLocales } from "../../components/property-locale-provider";
import schema from "../../gen/schema/witbooking.backoffice.accommodation.v1/Accommodation.json";

// PROTO
import { AccommodationAPIPromiseClient } from "../../gen/witbooking/backoffice/accommodation/v1/accommodation_api_grpc_web_pb";
import {
  Accommodation,
  CreateAccommodationRequest,
  DeleteAccommodationRequest,
  GetAccommodationRequest,
  GetAccommodationsRequest,
  Multimedia,
  MultimediaTranslation,
  ReorderAccommodationRequest,
  Translation as AccommodationTranslation,
  UpdateAccommodationRequest,
} from "../../gen/witbooking/backoffice/accommodation/v1/accommodation_api_pb";
import { getLanguageFromFullLocale, useConfig } from "../../lib/config";
import { SECTION } from "../../lib/constants";

// GRPC
import { useGrpcClient, usePropertyGrpcCall } from "../../lib/grpc";

// Rest
import { useHttpClient } from "../../lib/http";
import { regenPropertyCache } from "../common/api";

// Components
import { FormConfiguration } from "../shared/crud/form";
import { TableColumns } from "../shared/crud/list";
import SimpleCrud, { defaultCrudMessages } from "../shared/crud/simple-crud";
import { mapRenderer, StringRenderer } from "../shared/renderers/table";
import { HtmlEditorWidget } from "../shared/widgets/html";
import {
  getDataByActions,
  Image,
  imageManagerConfig,
  ImageManagerProvider,
  Translation,
} from "../shared/widgets/ImageManager";
import { SwitchGroup } from "../shared/widgets/SwitchGroup";
import { translationFieldConfig } from "../shared/widgets/translation";
import { BACKOFFICE_MANAGER_URL, BASE_URL } from "../witadmin/request";
import { FormContainer } from "./form-style";

// Translations
const accommodationsMessages = defineMessages({
  entityName: {
    id: "accommodation.entityName",
    defaultMessage: "Accommodations",
  },
  imagesHelpText: {
    id: "app.imagesHelpText",
    description: "Images Help Text",
    defaultMessage:
      "Las imágenes deben tener una resolución 1900 x 1080 pixeles y un tamaño máximo de 10MB.",
  },
});
const tableHeaderMessages = defineMessages({
  ticker: { id: "accommodation.ticker", defaultMessage: "Identifier" },
  translationsList: { id: "accommodation.name", defaultMessage: "Name" },
  description: { id: "accommodation.description", defaultMessage: "Description" },
});
export const roomTypeMessages = defineMessages({
  roomDenominationTypeUnit: {
    id: "roomDenominationType.unit",
    defaultMessage: "Unit",
  },
  roomDenominationTypeRooms: {
    id: "roomDenominationType.rooms",
    defaultMessage: "Rooms",
  },
  roomDenominationTypeApartments: {
    id: "roomDenominationType.apartments",
    defaultMessage: "Apartments",
  },
  roomDenominationTypeBungalows: {
    id: "roomDenominationType.bungalows",
    defaultMessage: "Bungalows",
  },
  roomDenominationTypeCabins: {
    id: "roomDenominationType.cabins",
    defaultMessage: "Cabins",
  },
  roomDenominationTypeHouses: {
    id: "roomDenominationType.houses",
    defaultMessage: "Houses",
  },
  roomDenominationTypeShipCabin: {
    id: "roomDenominationType.shipCabin",
    defaultMessage: "Ship cabin",
  },
  roomDenominationTypeVillas: {
    id: "roomDenominationType.villas",
    defaultMessage: "Villas",
  },
  roomDenominationTypeBeds: {
    id: "roomDenominationType.beds",
    defaultMessage: "Beds",
  },
});

const messages = {
  ...defaultCrudMessages,
  ...accommodationsMessages,
  tableHeaders: tableHeaderMessages,
};

// Interfaces
interface CustomAccommodation extends Omit<Accommodation.AsObject, "multimediaList"> {
  multimediaList: Image[];
}

// TODO: These methods could be generated automatically.
function translationFromObject(obj: AccommodationTranslation.AsObject): AccommodationTranslation {
  const result = new AccommodationTranslation();
  result.setLocale(obj.locale);
  result.setName(obj.name);
  result.setDescription(obj.description);

  return result;
}

function multimediaFromImage(multimediaList: Multimedia.AsObject[]): Image[] {
  return multimediaList.map(multimedia =>
    Object.assign(new File([""], multimedia.filename), {
      id: multimedia.id,
      url: multimedia.url,
      filename: multimedia.filename,
      translations:
        (multimedia.translationsList &&
          multimedia.translationsList.length &&
          multimedia.translationsList.reduce((accum, curr) => {
            const locale = curr.locale;
            return {
              ...accum,
              [locale]: {
                title: curr.title,
                description: curr.description,
              },
            };
          }, {})) ||
        {},
    })
  );
}

function accommodationFromCustomAccommodation({
  multimediaList,
  ...props
}: Accommodation.AsObject): CustomAccommodation {
  return {
    ...props,
    multimediaList: multimediaFromImage(multimediaList),
  } as CustomAccommodation;
}

function customMultimediaTranslationsFromMultimediaTranslations(
  translations: Translation = {}
): MultimediaTranslation[] {
  const results: MultimediaTranslation[] = Object.keys(translations).map(locale => {
    const multimediaTranslation = new MultimediaTranslation();
    multimediaTranslation.setLocale(locale);
    multimediaTranslation.setTitle(translations[locale].title);
    multimediaTranslation.setDescription(translations[locale].description || "");
    return multimediaTranslation;
  });
  return results;
}

function multimediaFromObject(obj: Image): Multimedia {
  const result = new Multimedia();
  result.setId(obj.id);
  result.setFilename(obj.filename);
  result.setTranslationsList(
    customMultimediaTranslationsFromMultimediaTranslations(obj.translations)
  );
  result.setUrl(obj.url);
  return result;
}

function accommodationFromObject(obj: CustomAccommodation): Accommodation {
  const result = new Accommodation();
  result.setId(obj.id);
  result.setTicker(obj.ticker);
  result.setType(obj.type);
  result.setTranslationsList(obj.translationsList.map(translationFromObject));
  result.setActive(obj.active);
  if (Array.isArray(obj?.multimediaList)) {
    result.setMultimediaList(
      // This is to save new order of images
      // but you cannot save the order of an image that is not loaded
      obj?.multimediaList?.filter(m => !m.url.includes("blob:")).map(multimediaFromObject)
    );
  }
  return result;
}

function AccommodationCrud({ propertyTicker, intl }: { propertyTicker: string; intl: IntlShape }) {
  const [accommodations, setAccommodations] = useState<CustomAccommodation[]>([]);
  const config = useConfig();

  // Grpc
  const grpcClient = useGrpcClient(AccommodationAPIPromiseClient);
  const getAccommodations = usePropertyGrpcCall(grpcClient, grpcClient.getAccommodations);
  const getAccommodation = usePropertyGrpcCall(grpcClient, grpcClient.getAccommodation);
  const createAccommodation = usePropertyGrpcCall(grpcClient, grpcClient.createAccommodation);
  const updateAccommodation = usePropertyGrpcCall(grpcClient, grpcClient.updateAccommodation);
  const deleteAccommodation = usePropertyGrpcCall(grpcClient, grpcClient.deleteAccommodation);
  const reorderAccommodation = usePropertyGrpcCall(grpcClient, grpcClient.reorderAccommodation);

  // Rest
  const httpClient = useHttpClient(BACKOFFICE_MANAGER_URL);
  const httpClientCache = useHttpClient(BASE_URL || "");

  const allowedLocales = usePropertyLocales();
  const customSchema = useMemo(
    () => ({
      ...schema,
      properties: {
        ...schema.properties,
        multimedia: {
          ...schema.properties.multimedia,
          items: {
            ...schema.properties.multimedia.items,
            properties: {
              ...schema.properties.multimedia.items.properties,
              translations: {
                ...schema.properties.multimedia.items.properties.translations,
                items: {
                  ...schema.properties.multimedia.items.properties.translations.items,
                  required: ["title"],
                },
                type: "object",
              },
            },
            additionalProperties: true,
          },
        },
      },
    }),
    []
  );

  const featchCreateImages = useCallback(
    async (accommodationId: string, data: Image[] = []) => {
      if (data.length) {
        const formData = new FormData();
        data.forEach(d => {
          formData.append("images", d, d.id);
          formData.append(
            "medias",
            new Blob(
              [
                JSON.stringify({
                  id: d.id,
                  filename: d.filename.toLowerCase(),
                  translations: customMultimediaTranslationsFromMultimediaTranslations(
                    d.translations
                  ),
                }),
              ],
              {
                type: "application/json",
              }
            )
          );
        });
        await httpClient.post<FormData, Multimedia.AsObject>(
          `/v1/${propertyTicker}/accommodation/${accommodationId}/media/multi`,
          formData
        );
      }
    },
    [httpClient, propertyTicker]
  );

  const featchRemoveImages = useCallback(
    async (accommodationId: string, data: Image[]) => {
      if (data.length) {
        const request = data.map(d => d.id);
        await httpClient.delete<any>(
          `/v1/${propertyTicker}/accommodation/${accommodationId}/media?${queryString.stringify({
            mediaIds: request,
          })}`
        );
      }
    },
    [httpClient, propertyTicker]
  );

  const featchUpdateImages = useCallback(
    async (accommodationId: string, data: Image[]) => {
      if (data.length) {
        const request = data.map(d => ({
          ...d,
          translations: customMultimediaTranslationsFromMultimediaTranslations(d.translations),
        }));
        await httpClient.put<any, Multimedia.AsObject>(
          `/v1/${propertyTicker}/accommodation/${accommodationId}/media`,
          request
        );
      }
    },
    [httpClient, propertyTicker]
  );

  const featchUploadImages = useCallback(
    async ({ id, multimediaList }: CustomAccommodation) => {
      const currentAccommodation = accommodations.find(p => p.id === id);
      if (!currentAccommodation) {
        return await featchCreateImages(id, multimediaList);
      }
      const { create, update, remove } = getDataByActions(
        currentAccommodation.multimediaList,
        multimediaList
      );
      return await Promise.all([
        featchCreateImages(id, create),
        featchRemoveImages(id, remove),
        featchUpdateImages(id, update),
      ]);
    },
    [accommodations, featchRemoveImages, featchCreateImages, featchUpdateImages]
  );

  const handleGetAll = useCallback(async (): Promise<CustomAccommodation[]> => {
    const request = new GetAccommodationsRequest();
    const response = await getAccommodations(request);
    const result = response
      .getAccommodationList()
      .map(e => accommodationFromCustomAccommodation(e.toObject()));
    setAccommodations(result);
    return result;
  }, [getAccommodations, setAccommodations]);

  const handleGetOne = useCallback(
    async (id: string) => {
      const request = new GetAccommodationRequest();
      request.setAccommodationId(id);
      const response = await getAccommodation(request);
      const result = accommodationFromCustomAccommodation(
        response.getAccommodation()?.toObject()!!
      );
      setAccommodations([result]);
      return result;
    },
    [getAccommodation, setAccommodations]
  );

  const handleCreate = useCallback(
    async (entity: CustomAccommodation) => {
      const request = new CreateAccommodationRequest();
      request.setAccommodation(accommodationFromObject(entity));
      const respose = await createAccommodation(request);
      await featchUploadImages(entity);

      return respose.getAccommodation()?.getId()!!;
    },
    [createAccommodation, featchUploadImages]
  );

  const handleUpdate = useCallback(
    async (entity: CustomAccommodation) => {
      const request = new UpdateAccommodationRequest();
      request.setAccommodation(accommodationFromObject(entity));
      await Promise.all([updateAccommodation(request), featchUploadImages(entity)]);
      await regenPropertyCache(httpClientCache, propertyTicker);
    },
    [updateAccommodation, featchUploadImages, httpClientCache, propertyTicker]
  );

  const handleDelete = useCallback(
    async (entityId: string) => {
      const request = new DeleteAccommodationRequest();
      request.setAccommodationId(entityId);
      await deleteAccommodation(request);
    },
    [deleteAccommodation]
  );

  const handleReorder = useCallback(
    async (id: string, after?: string) => {
      const request = new ReorderAccommodationRequest();
      request.setAccommodationId(id);
      request.setAfterId(after || "");

      await reorderAccommodation(request);
    },
    [reorderAccommodation]
  );

  const formConfiguration = useMemo<FormConfiguration<CustomAccommodation>>(() => {
    return {
      __fieldOrder: ["ticker", "active", "type", "translationsList", "multimediaList", "*"],
      __useProtoStyleArrays: true,
      id: {
        default: () => uuidv4(),
        hidden: true,
      },
      ticker: {
        readonly: isNew => !isNew,
        messages: {
          label: defineMessage({ id: "accommodation.ticker", defaultMessage: "Identifier" }),
        },
      },
      active: {
        messages: {
          label: defineMessage({ id: "accommodation.active", defaultMessage: "active" }),
        },
        default: () => true,
        widget: SwitchGroup,
      },
      type: {
        placeholder: defineMessage({
          id: "accommodation.type.placeholder",
          defaultMessage: "Select type of accommodation",
        }),
        messages: {
          label: defineMessage({ id: "accommodation.type", defaultMessage: "type" }),
        },
        enumLabels: [
          roomTypeMessages.roomDenominationTypeUnit,
          roomTypeMessages.roomDenominationTypeRooms,
          roomTypeMessages.roomDenominationTypeApartments,
          roomTypeMessages.roomDenominationTypeBungalows,
          roomTypeMessages.roomDenominationTypeCabins,
          roomTypeMessages.roomDenominationTypeHouses,
          roomTypeMessages.roomDenominationTypeShipCabin,
          roomTypeMessages.roomDenominationTypeVillas,
          roomTypeMessages.roomDenominationTypeBeds,
        ],
      },
      translationsList: {
        ...translationFieldConfig(allowedLocales || []),
        children: {
          __fieldOrder: ["name", "description", "*"],
          name: {
            messages: {
              label: defineMessage({
                id: "accommodation.translation.name",
                defaultMessage: "Name",
              }),
            },
          },
          description: {
            widget: HtmlEditorWidget,
            messages: {
              label: defineMessage({
                id: "accommodation.translation.description",
                defaultMessage: "Description",
              }),
            },
          },
          ...translationFieldConfig(allowedLocales || []).children,
        },
        messages: {
          label: defineMessage({
            id: "accommodation.translations",
            defaultMessage: "Translations",
          }),
        },
      },
      multimediaList: {
        ...imageManagerConfig({
          locales: allowedLocales,
          maxSizeMB: config.mediaMaxSizeMB,
          maxFiles: config.mediaMaxFiles,
          minWidth: config.mediaMinWidth,
          helpText: messages.imagesHelpText,
        }),
        messages: {
          label: defineMessage({ id: "accommodation.images", defaultMessage: "Images management" }),
        },
      },
    };
  }, [allowedLocales, config.mediaMaxFiles, config.mediaMaxSizeMB, config.mediaMinWidth]);

  const tableColumns = useMemo<TableColumns<CustomAccommodation>>(() => {
    return {
      ticker: {
        renderer: StringRenderer,
      },
      translationsList: {
        children: {
          name: {
            renderer: mapRenderer(StringRenderer, (v, intl) => {
              if (!v || !v.length) return undefined;
              const current = v.find(t => t.locale === getLanguageFromFullLocale(intl.locale));
              return current?.name || v[0].name;
            }),
          },
          description: {
            renderer: mapRenderer(
              StringRenderer,
              (v, intl) => {
                if (!v || !v.length) return undefined;
                const current = v.find(t => t.locale === getLanguageFromFullLocale(intl.locale));
                return current?.description || v[0].description;
              },
              { isHtml: true, maxCharacters: 200 }
            ),
          },
        },
      },
    };
  }, []);

  const validate = useCallback(
    (formData, errors) => {
      if (!formData.multimediaList || formData.multimediaList.length < 1) {
        errors.multimediaList.addError(intl.formatMessage(messages.requiredImages));
      }
      return errors;
    },
    [intl]
  );

  return (
    <ImageManagerProvider>
      <SimpleCrud<CustomAccommodation>
        formContainer={FormContainer}
        messages={messages}
        onGetAll={handleGetAll}
        onGetOne={handleGetOne}
        onCreate={handleCreate}
        onUpdate={handleUpdate}
        onDelete={handleDelete}
        onReorder={handleReorder}
        formConfiguration={formConfiguration}
        schema={customSchema}
        tableColumns={tableColumns}
        section={SECTION.ACCOMMODATIONS}
        validate={validate}
      />
    </ImageManagerProvider>
  );
}

export default injectIntl(withAuthorizationVerifier(AccommodationCrud));
